import React, { useMemo } from "react";

import { useHistory } from "react-router-dom";

import { openDealReview } from "manager/components/vero1/openDealReview";
import { resolveVero1Banner } from "manager/components/vero1/vero1BannerState";
import {
  recommendationDecisionButton,
  recommendationSeverity,
  runVerdictColor,
  scopeBands,
  scopeChangedItems,
} from "./recommendationScope";
import { Vero1ChangedItems } from "manager/components/vero1/Vero1ChangedItems";
import { Vero1HoldBand } from "manager/components/vero1/Vero1HoldBand";
import { Vero1LumenAmendedRow } from "manager/components/vero1/Vero1LumenAmendedRow";
import { Vero1RereviewBand } from "manager/components/vero1/Vero1RereviewBand";
import { useVero1Viewer } from "manager/components/vero1/vero1Viewer";
import {
  useVero1StatusSettling,
} from "manager/hooks/api/vero1Queries";
import { useIsViewOnlyUser } from "manager/hooks/useIsViewOnlyUser";
import { VERO1_COSIGN_HOLD_REASON } from "manager/interfaces/api";
import FadeIn from "shared/components/animated/FadeIn";
import FlexContainer from "shared/components/FlexContainer";
import { MediumTitle } from "shared/components/Typography";
import { LumenIcon } from "shared/components/vega/icons";
import { LumenV2Header } from "shared/components/vega/LumenV2";
import {
  selectOutcomeBands,
  titleCaseLabel,
} from "shared/components/vega/LumenV2/utils";
import type { VeroCopilotRef } from "shared/components/VeroCopilot";
import { Permission } from "shared/graphql";

import { useActiveCompany } from "shared/hooks";
import type {
  Vero1BannerState,
  Vero1ChangedItems as Vero1ChangedItemsData,
} from "shared/interfaces/api/vero1";
import { bannerDate } from "shared/utils/bannerDate";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";

import { COLORS } from "../../../../shared/config/constants";
import { categoryGroups } from "../AppListV2/constants";
import { categoryProcessorMap } from "../AppListV2/LumenSummary/helpers/categoryProcessors";
import { findLumenResult } from "../AppListV2/queryHelpers";
import { ApplicantSummary } from "../modals";
import { ApproveStatus, AppStatus } from "../types";

import { useDealSummaryRedesign } from "../useDealSummaryRedesign";

import {
  AwaitingApplicantsStrip,
  awaitingApplicantsMeta,
} from "./AwaitingApplicantsStrip";
import { ActionButton, ActionPopover } from "./blocks";
import { DealBanner, DealBannerSlot } from "./DealBanner";
import { VerdictSkeleton } from "./DealBanner/styled";
import { ButtonType, LeasingDecision, LumenItem } from "./interfaces";
import { LumenBannerStrip, LumenStripSection } from "./LumenBannerStrip";
import { PmsApprovedNoVeroBanner } from "./pms-approved-no-vero-banner";
import {
  MoreActionsButton,
  ResponsiveFlexContainer,
  WellContainer,
} from "./styled";
import { useActionBar } from "./use-action-bar";
import { getDefaultButtonByStatus } from "./utils";

export interface LumenActionBarProps {
  score?: string;
  items?: LumenItem[];
  tooltip?: string;
  pending?: boolean;
}

export interface ActionBarProps {
  dealId?: string | number;
  showLumen: boolean;
  /* the factor-cards sheet renders directly below the banner */
  cardsDocked?: boolean;
  vero1Active?: boolean;
  vero1BannerState?: Vero1BannerState | null;
  vero1ChangedItems?: Vero1ChangedItemsData | null;
  dealLabel?: string;
  status?: AppStatus;
  approved?: ApproveStatus;
  statusLabel?: string;
  lumen?: LumenActionBarProps;
  permission?: Permission;
  refetch?: () => void;
  applicantsSummary: ApplicantSummary[];
  leasingDecision?: LeasingDecision | null;
  conditionalProceed: boolean;
  missingUnit?: boolean;
  countryCriminalSearchInProgress?: boolean;
  criminalSearchInProgress?: boolean;
  backgroundReportsUnavailable?: boolean;
  ocrProcessing?: boolean;
  canManuallySendToPms?: boolean;
  hasBeenSentToEntrata?: boolean;
  isPmsApprovedWithoutVeroCompletion?: boolean;
  pmsApprovedAt?: string;
  // While the deal is still loading, `deal.applications` is empty so the
  // VERO-completion signal is not yet reliable. Suppress the banner
  // until load completes to avoid it flashing on and then off.
  loading?: boolean;
  lastSentToPmsAt?: Date;
  lumenRun?: any;
  provider?: string;
  copilotRef?: React.RefObject<VeroCopilotRef>;
  // Cosign props
  canInitiateCosign?: boolean;
  primaryApplicationId?: string | number;
  applicants?: any[];
  isLumenStillCalculating?: boolean;
  cosignApplication?: any;
  primaryApplicantCosignBlockInfo?: {
    verifiedIncome?: number;
    rent?: number;
  };
  cosignEligibilityReasons?: string[];
  downloadApprovalLetter?: () => void;
}

export const ActionBar = (props: ActionBarProps) => {
  const { status, canInitiateCosign } = props;
  const {
    showLumen,
    statusLabel,
    permission,
    lumenRun,
    copilotRef,
    applicants,
    isLumenStillCalculating,
    cosignApplication,
    primaryApplicantCosignBlockInfo,
    cosignEligibilityReasons,
    isPmsApprovedWithoutVeroCompletion,
    pmsApprovedAt,
    loading,
    approved,
    conditionalProceed,
  } = props;
  const showPmsApprovedNoVeroBanner =
    Boolean(isPmsApprovedWithoutVeroCompletion) && !loading;
  const useDealsListUiRedesign = useVeroFlag(VeroFlag.useDealsListUiRedesign);
  const useDealSummaryRedesignShell = useDealSummaryRedesign();
  const veroCopilot = useVeroFlag(VeroFlag.veroCopilot);

  const {
    actions,
    button,
    buttons,
    visiblePopover,
    onVisibleChange,
    canManuallySendToPms,
    hasBeenSentToEntrata,
    lumenSubtitle,
    status: buttonStatus,
  } = useActionBar(props);

  const { isViewOnly } = useIsViewOnlyUser();
  const isSubmitted = [AppStatus.Done, AppStatus.Submitted].includes(status);

  const history = useHistory();
  const { canViewInternal } = useVero1Viewer();
  const { activeCompany } = useActiveCompany();
  // VERO1 content gates on the VERO1 config alone (vero1Active) — no feature
  // flag. If the deal is a VERO1 deal, leasing acts on the recommendation.
  const vero1Banner = props.vero1Active
    ? resolveVero1Banner(props.vero1BannerState, canViewInternal)
    : null;
  // Leasing never sees raw Lumen while VERO1 governs the deal.
  const showLumenHeader =
    showLumen && isSubmitted && !vero1Banner?.hideLumenFromViewer;
  const holdStrip =
    vero1Banner?.showInternalHold && props.dealId != null ? (
      <Vero1HoldBand
        dealId={props.dealId}
        vero1Active={props.vero1Active}
        dealLabel={props.dealLabel}
        runAt={(props.vero1BannerState as { runAt?: string | null })?.runAt}
      />
    ) : undefined;
  // The strip surfaces the verdict's own severity only (PRODUCT-2779, revised
  // 2026-09-14): a decline carries fail items and fail facts, a conditional
  // warn, an approval the rows the analyst moved to pass — never the tier
  // beneath. The agent's explanation Lumen appends to a picked outcome already
  // sits on the changed-items row, so it is not repeated as a why-line.
  const vero1Verdict = (() => {
    const bs = props.vero1BannerState;
    return bs?.state === "recommendation" ? bs.recommendation : null;
  })();
  const vero1Severity = recommendationSeverity(vero1Verdict);
  const scopedChangedItems = props.vero1ChangedItems
    ? scopeChangedItems(props.vero1ChangedItems, vero1Severity)
    : undefined;
  const vero1WhyLines = (() => {
    if (!vero1Banner?.showRecommendation || !vero1Severity) return undefined;
    const findings = new Set(
      (props.vero1ChangedItems?.items ?? [])
        .map((item) => item.findings?.trim())
        .filter(Boolean)
    );
    const lines = Array.from(
      new Set(
        scopeBands(selectOutcomeBands(lumenRun), vero1Severity)
          .flatMap((band) => band.recommendations ?? [])
          .filter((line) => !findings.has(line.trim()))
      )
    );
    return lines.length ? lines : undefined;
  })();
  const hasRecommendationStrip = Boolean(
    (scopedChangedItems &&
      (scopedChangedItems.rationale || scopedChangedItems.items.length > 0)) ||
      vero1WhyLines
  );
  const amendedRow =
    vero1Banner?.showRecommendation && props.dealId != null ? (
      <Vero1LumenAmendedRow
        dealId={props.dealId}
        vero1Active={props.vero1Active}
        standalone={!hasRecommendationStrip}
      />
    ) : undefined;
  const recommendationStrip =
    vero1Banner?.showRecommendation && hasRecommendationStrip ? (
      <Vero1ChangedItems
        data={
          scopedChangedItems ?? {
            recommendation: null,
            rationale: "",
            items: [],
          }
        }
        whyLines={vero1WhyLines}
        amendedRow={amendedRow}
        onViewDetails={
          props.dealId != null
            ? () => openDealReview(history, props.dealId)
            : undefined
        }
      />
    ) : (
      amendedRow
    );
  const applicantSummaries = props.applicantsSummary ?? [];
  // a submitted recommendation is the governing record — awaiting only masks
  // the hold states, never a verdict or a terminal disposition
  const awaitingApplicants =
    vero1Banner != null &&
    !vero1Banner.showRecommendation &&
    !vero1Banner.terminal &&
    !isSubmitted;

  // a status write's refresh is in flight: verdict skeleton, not the
  // outgoing status
  const vero1StatusSettling = useVero1StatusSettling(props.dealId);

  const awaitingMeta =
    applicantSummaries.length > 0
      ? awaitingApplicantsMeta(applicantSummaries)
      : undefined;
  // The enacted leasing decision as a second labelled slot (spec §01 "after
  // leasing enacts a decision") — a record beside the recommendation.
  const leasingDecisionSlot: DealBannerSlot | null =
    (vero1Banner
      ? vero1Banner.showRecommendation && !awaitingApplicants
      : showLumen && isSubmitted) && props.leasingDecision
      ? {
          eyebrow: "Leasing decision",
          verdict: props.leasingDecision.label,
          verdictColor:
            props.leasingDecision.tone === "approve"
              ? "#67D6A6"
              : props.leasingDecision.tone === "decline"
              ? "#F0728C"
              : undefined,
          meta: props.leasingDecision.at
            ? `${bannerDate(props.leasingDecision.at)}.`
            : undefined,
        }
      : null;
  const verdictButton = vero1Banner?.showRecommendation
    ? recommendationDecisionButton(vero1Verdict)
    : undefined;
  const decisionButton =
    verdictButton === ButtonType.cancel
      ? ButtonType.cancel
      : verdictButton !== undefined
      ? getDefaultButtonByStatus({
          approved,
          canManuallySendToPms,
          status: buttonStatus ?? status,
          showLumen,
          score: "",
          showConditionalProceed: Boolean(conditionalProceed),
          verdict: verdictButton,
        })
      : button;
  const showDecisionAction =
    !vero1StatusSettling &&
    (!vero1Banner ||
      (vero1Banner.showRecommendation && !awaitingApplicants) ||
      decisionButton === ButtonType.cancel);

  const handleOpenCopilot = () => {
    copilotRef?.current?.open();
  };

  // Get the overall application result (not per-applicant)
  const applicationLumenResult = findLumenResult(lumenRun);

  // Generate results for each applicant
  const applicantLumenResults =
    applicants?.map((applicant) => {
      const result = findLumenResult(lumenRun, applicant.id);
      return {
        applicantId: applicant.id,
        applicantName: `${applicant.firstName} ${applicant.lastName}`,
        lumenResult: result,
      };
    }) || [];

  const eligibleForCosign = useMemo(() => {
    let idVerificationIsFailing = false;

    categoryGroups.forEach(({ id, name }) => {
      const processor = categoryProcessorMap[id];
      const { status } = processor({
        applicationLumenResult,
        applicantLumenResults,
        isLumenStillCalculating,
        lumenRun,
      });
      if (name === "ID Verification" && status !== "pass") {
        idVerificationIsFailing = true;
      }
    });

    // Return true only if id verification is not failing
    return !idVerificationIsFailing;
  }, [
    applicationLumenResult,
    applicantLumenResults,
    isLumenStillCalculating,
    lumenRun,
  ]);
  const cosignHeldForVero1Review = Boolean(
    cosignEligibilityReasons?.includes(VERO1_COSIGN_HOLD_REASON)
  );

  const showNewBanner = useDealSummaryRedesignShell || vero1Banner != null;

  const statusInline = showNewBanner && showLumen && isSubmitted;
  // Non-VERO1 slot content (status label / Lumen header / PMS banner). When a
  // VERO1 verdict exists it takes the slot instead (see DealBanner props below).
  const awaiting =
    showNewBanner &&
    showLumen &&
    !isSubmitted &&
    status != null &&
    !showPmsApprovedNoVeroBanner &&
    !vero1Banner;

  const lumenStrip: React.ReactNode = (() => {
    if (!statusInline) return undefined;
    const sections: LumenStripSection[] = [];
    if (permission?.noApprove && permission?.noApproveReason) {
      sections.push({
        tone: "blocked",
        eyebrow: "Blocked",
        lines: [permission.noApproveReason],
      });
    }
    // the bands at the verdict's own severity, fail first, titled with the
    // client's label — a tier beneath the verdict stays on its factor card
    const bands = selectOutcomeBands(lumenRun);
    scopeBands(bands, runVerdictColor(lumenRun, bands)).forEach((band) => {
      const label = titleCaseLabel(band.scoreLabel);
      if (!label) return;
      sections.push({
        tone: band.scoreLabelColor === "fail" ? "fail" : "warn",
        eyebrow: `${label} criteria met`,
        lines: band.recommendations ?? [],
      });
    });
    return sections.length ? <LumenBannerStrip sections={sections} /> : undefined;
  })();

  const statusContent = (
    <>
      {showLumenHeader && (
        <LumenV2Header
          lumenRun={lumenRun}
          eligibleForCosign={
            eligibleForCosign && canInitiateCosign && !cosignHeldForVero1Review
          }
          cosignApplication={cosignApplication}
          lgTone={showNewBanner}
        />
      )}
      {showPmsApprovedNoVeroBanner ? (
        <PmsApprovedNoVeroBanner approvedAt={pmsApprovedAt} />
      ) : statusInline ? null : (
        <div className="vega title invert">{statusLabel}</div>
      )}
      {showLumen && !isSubmitted && !awaiting && (
        <div className="vega info invert">{lumenSubtitle}</div>
      )}

      {hasBeenSentToEntrata && (
        <MediumTitle
          strong
          style={{
            color: COLORS.violetTheme.lightGreen,
            alignSelf: "center",
          }}
        >
          APPLICATION SENT TO ENTRATA
        </MediumTitle>
      )}
    </>
  );

  const statusSlot = statusContent;

  const actionCluster = (
    <FlexContainer alignItems="center" style={{ gap: "8px" }}>
      {showDecisionAction && !awaiting && (
        <ActionButton
          actions={actions}
          button={decisionButton}
          permission={permission}
          lgPri={showNewBanner}
        />
      )}

      {!isViewOnly && (
        <ActionPopover
          actions={actions}
          buttons={buttons}
          skip={awaiting ? undefined : decisionButton}
          permission={permission}
          visiblePopover={visiblePopover}
          onVisibleChange={onVisibleChange}
          cosignApplication={cosignApplication}
          primaryApplicantCosignBlockInfo={primaryApplicantCosignBlockInfo}
          cosignEligibilityReasons={cosignEligibilityReasons}
          lgPri={showNewBanner}
        >
          <MoreActionsButton type="primary" $lgPri={showNewBanner}>
            MORE ACTIONS
          </MoreActionsButton>
        </ActionPopover>
      )}

      {!isViewOnly && useDealsListUiRedesign && veroCopilot && (
        <MoreActionsButton
          type="primary"
          $lgPri={showNewBanner}
          onClick={handleOpenCopilot}
          style={{
            padding: "10px",
            minWidth: "auto",
            aspectRatio: "1 / 1",
            width: "fit-content",
          }}
          title="Open Vero Copilot"
        >
          <LumenIcon
            style={{
              width: "24px",
              height: "24px",
              color: "white",
              fill: "white",
            }}
          />
        </MoreActionsButton>
      )}
    </FlexContainer>
  );

  return (
    <>
      {!showNewBanner && (
        <Vero1RereviewBand
          dealId={props.dealId}
          vero1Active={props.vero1Active}
        />
      )}
      {showNewBanner ? (
        <DealBanner
          actions={actionCluster}
          docked={props.cardsDocked}
          topBand={
            // the hold band absorbs the re-screen message, so the notice
            // band yields whenever it shows
            holdStrip ? undefined : (
              <Vero1RereviewBand
                dealId={props.dealId}
                vero1Active={props.vero1Active}
              />
            )
          }
          strip={
            vero1Banner ? (
              vero1StatusSettling ? undefined : awaitingApplicants ? (
                applicantSummaries.length > 0 ? (
                  <AwaitingApplicantsStrip applicants={applicantSummaries} />
                ) : undefined
              ) : (
                holdStrip ??
                recommendationStrip
              )
            ) : awaiting && applicantSummaries.length > 0 ? (
              <AwaitingApplicantsStrip applicants={applicantSummaries} />
            ) : (
              lumenStrip
            )
          }
          secondary={leasingDecisionSlot}
          {...(vero1Banner
            ? vero1StatusSettling
              ? {
                  // same shimmer as the initial load — the outgoing status
                  // must not linger while the refresh lands
                  eyebrow: "Recommendation",
                  verdict: <VerdictSkeleton data-testid="verdict-skeleton" />,
                }
              : awaitingApplicants
              ? {
                  verdict: "Awaiting applicant",
                  meta: awaitingMeta,
                }
              : {
                  // only a real recommendation gets the "Recommendation"
                  // eyebrow; hold states (pending / under review / not
                  // applied) are a status, so they carry no label unless the
                  // banner names one (e.g. the identity-close disposition)
                  eyebrow:
                    vero1Banner.eyebrow ??
                    (vero1Banner.showRecommendation ? "Recommendation" : undefined),
                  verdict: vero1Banner.label,
                  verdictColor: vero1Banner.verdictColor ?? undefined,
                  meta: vero1Banner.subtext,
                }
            : loading
            ? {
                // deal payload not in yet — shimmer where the verdict goes
                eyebrow: "Recommendation",
                verdict: <VerdictSkeleton data-testid="verdict-skeleton" />,
              }
            : awaiting
            ? {
                verdict: "Awaiting applicant",
                meta: awaitingMeta,
              }
            : {})}
        >
          {statusSlot}
        </DealBanner>
      ) : (
        <WellContainer showLumen>
          <FadeIn>
            <ResponsiveFlexContainer
              justifyContent="space-between"
              fullHeight
              alignItems="center"
              gap="12px"
            >
              {statusSlot}
              {actionCluster}
            </ResponsiveFlexContainer>
          </FadeIn>
        </WellContainer>
      )}
    </>
  );
};
